import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import Collapse from '@material-ui/core/Collapse';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getStatus, getMethod } from '@constants/transactions';
import { getFormatedDate, capitalize, moneyFormat } from '@helpers';
import { Icon } from 'fpay-ui';
import { useBreakpoint } from '@hooks';

const Divider = styled.hr`
  border: none;
  border-top: solid 1px #e3e3e3;
  margin: 16px 0;
`;

const Label = styled.div(
  ({ theme }) => css`
    font-size: 12px;
    line-height: 1.33;
    font-weight: normal;
    color: ${theme.colors.gray800};
    margin-bottom: 4px;
  `
);

const ParagraphStyled = styled.div(
  ({ theme }) => css`
    font-size: 14px;
    line-height: 1.36;
    font-weight: 700;
    color: ${theme.colors.gray800};
  `
);

const StyledIcon = styled(Icon)(
  ({ open }) => css`
    position: relative;
    width: 20px;
    height: 20px;
    top: 5px;
    margin-left: 3px;
    transition: transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
    ${open &&
    css`
      transform: rotate(180deg);
    `}
  `
);

const CollapseText = styled.div(
  ({ theme }) => css`
    ${theme.textTypes['Body-2-w400']}
    text-align: right;
    margin-top: -8px;
    margin-bottom: 32px;
    color: ${theme.colors.gray700};
    cursor: pointer;
  `
);

const RefundCard = ({ marginTop = '24px', detailAlwaysOpen = false }) => {
  const { mobile } = useBreakpoint();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { currentRefund, originalTransactionSaleAmount } = useSelector(
    (state) => state.transactions
  );
  // depende si el valor se obtuvo de la busqueda al devolver o al apretar detalles
  const originalTransactionSaleAmountToDisplay = moneyFormat(
    currentRefund?.transactionOriginal?.total ?? originalTransactionSaleAmount
  );

  const handleCollapseOpen = () => {
    setOpen(!open);
  };
  const isMobileDetailTransaction = mobile && detailAlwaysOpen;
  const mobileStyles = isMobileDetailTransaction && { display: 'none' };
  return (
    <Grid container style={{ marginTop }}>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} container>
        <Grid item xs={isMobileDetailTransaction ? 8 : 6} style={{ marginBottom: '16px' }}>
          <Label data-testid="label-refund-user-name">{t('transactions.refunds.user')}</Label>
          <ParagraphStyled data-testid="refund-user-name">
            {currentRefund.user
              ? capitalize(`${currentRefund.user.first_name} ${currentRefund.user.last_name}`)
              : '-'}
          </ParagraphStyled>
        </Grid>

        <Grid
          item
          xs={isMobileDetailTransaction ? 4 : 6}
          align="right"
          style={{ marginBottom: '16px' }}
        >
          <Label data-testid="label-refund-authorization">{t('transactions.refunds.code')}</Label>
          <ParagraphStyled data-testid="refund-authorization">
            {currentRefund.data?.length ? currentRefund.data[0]?.authorization_id : '-'}
          </ParagraphStyled>
        </Grid>

        <Grid item xs={6} align="left" style={{ marginBottom: '16px' }}>
          <Label data-testid="label-refund-date">{t('transactions.refunds.date')}</Label>
          <ParagraphStyled data-testid="refund-date">
            {currentRefund.data?.length
              ? getFormatedDate(currentRefund.data[0].event_date, 'dd/MM/yyyy - HH:mm\\h\\s')
              : '-'}
          </ParagraphStyled>
        </Grid>

        <Grid item xs={6} align="right">
          <Label data-testid="label-refund-gross-value">
            {t('transactions.refunds.totalValue')}
          </Label>
          <ParagraphStyled data-testid="refund-gross-value">
            {currentRefund.data?.length
              ? moneyFormat(
                  Math.abs(currentRefund.data.reduce((acc, item) => item.gross_value + acc, 0))
                )
              : '-'}
          </ParagraphStyled>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Collapse in={open || detailAlwaysOpen}>
          <Grid item xs={12} style={{ marginBottom: '16px' }}>
            <ParagraphStyled data-testid="label-transaction-title">
              {t('transactions.refunds.transactionOriginal.title')}
            </ParagraphStyled>
          </Grid>
          <Grid item xs={12} container>
            <Grid item xs={6} style={{ marginBottom: '16px' }}>
              <Label data-testid="label-transaction-date">{t('transactions.refunds.date')}</Label>
              <ParagraphStyled data-testid="transaction-date">
                {currentRefund?.transactionOriginal?.event_date
                  ? getFormatedDate(
                      currentRefund?.transactionOriginal?.event_date,
                      'dd/MM/yyyy - HH:mm\\h\\s'
                    )
                  : '-'}
              </ParagraphStyled>
            </Grid>
            <Grid item xs={6} align="right" style={{ marginBottom: '16px', ...mobileStyles }}>
              <Label data-testid="label-transaction-authorization">
                {t('transactions.refunds.transactionOriginal.code')}
              </Label>
              <ParagraphStyled data-testid="transaction-authorization">
                {currentRefund?.transactionOriginal?.authorization_id || '-'}
              </ParagraphStyled>
            </Grid>
            <Grid item xs={6} style={{ marginBottom: '16px', ...mobileStyles }}>
              <Label data-testid="label-transaction-type">
                {t('transactions.refunds.transactionOriginal.type')}
              </Label>
              <ParagraphStyled data-testid="transaction-type">
                {currentRefund?.transactionOriginal?.type
                  ? getStatus(currentRefund?.transactionOriginal?.type)
                  : '-'}
              </ParagraphStyled>
            </Grid>
            <Grid item xs={6} align="right" style={{ marginBottom: '16px' }}>
              <Label data-testid="label-transaction-operation">
                {t('transactions.refunds.transactionOriginal.paymentMethod')}
              </Label>
              <ParagraphStyled data-testid="transaction-operation">
                {currentRefund?.transactionOriginal?.operation
                  ? getMethod(currentRefund?.transactionOriginal?.operation)
                  : '-'}
              </ParagraphStyled>
            </Grid>
            <Grid item xs={6}>
              <Label data-testid="label-transaction-installments">
                {t('transactions.refunds.transactionOriginal.installments')}
              </Label>
              <ParagraphStyled data-testid="transaction-installments">
                {currentRefund?.transactionOriginal?.number_of_installments || '-'}
              </ParagraphStyled>
            </Grid>
            <Grid item xs={6} align="right">
              <Label data-testid="label-transaction-gross-total-value">
                {t('transactions.refunds.totalValue')}
              </Label>
              <ParagraphStyled data-testid="transaction-gross-total-value">
                {currentRefund?.transactionOriginal?.gross_value &&
                currentRefund?.transactionOriginal?.number_of_installments
                  ? originalTransactionSaleAmountToDisplay
                  : '-'}
              </ParagraphStyled>
            </Grid>
          </Grid>
          {!detailAlwaysOpen && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
        </Collapse>
      </Grid>
      {!detailAlwaysOpen && (
        <Grid item xs={12}>
          <CollapseText data-testid="action-of-detail" onClick={handleCollapseOpen}>
            Ver {open ? 'menos' : 'más'}
            <StyledIcon icon="ic-arrow-down" color="gray700" open={open} />
          </CollapseText>
        </Grid>
      )}
    </Grid>
  );
};

export default RefundCard;
